/**
 * Shared mutation-diff helpers for the geometry reducer family.
 *
 * Phase 3b PR #5 — `geometryReducer.ts` migrates its 88-case switch
 * to the `ReducerMutationResult` contract. Many of the cases delegate
 * to helpers (block authoring, attribute editing, dynamic-block authoring,
 * BPM, lookup tables, array, xref, purge/rename block, explode-insert)
 * that today return a bare `DrawingDocument` with no closed-form
 * "what changed" predicate. For those we diff the pre/post documents
 * to derive the mutation; the diff cost is amortised against the cost
 * of `assembleDrawingDocument` (which the helpers already pay).
 *
 * For inline create-* cases (~20) `singleAddedMutation` covers the common
 * "append one entity" shape without scanning the document.
 *
 * The helpers here intentionally mirror the private versions in
 * `geometryReducerEdit.ts` and `geometryReducerEditTrim.ts`. PR #6
 * will fold those duplicates back into this module once the contract
 * is fully wired through dispatch.
 */

import {
  drawingEntitySchema,
  updateEntityById,
  type DrawingDocument,
  type DrawingEntity,
  type DrawingEntityInput,
} from "@dxf-ai-editor/drawing-model";

import {
  NO_MUTATIONS,
  type EntityMutation,
  type StyleTableTouchKind,
} from "./types";

interface MutableMutation {
  added: Array<{ spaceId: string; entity: DrawingEntity }>;
  removed: Array<{ spaceId: string; entityId: string }>;
  updated: Array<{
    spaceId: string;
    prev: DrawingEntity;
    next: DrawingEntity;
  }>;
}

function emptyMutable(): MutableMutation {
  return { added: [], removed: [], updated: [] };
}

function freezeMutation(
  m: MutableMutation,
  styleTablesTouched: ReadonlySet<StyleTableTouchKind> = NO_MUTATIONS.styleTablesTouched,
): EntityMutation {
  if (
    m.added.length === 0 &&
    m.removed.length === 0 &&
    m.updated.length === 0 &&
    styleTablesTouched.size === 0
  ) {
    return NO_MUTATIONS;
  }
  return {
    added: m.added,
    removed: m.removed,
    updated: m.updated,
    styleTablesTouched,
  };
}

/**
 * Structural deep-equal for entity payloads. Used by
 * `diffSpaceEntities` so survivors of an `assembleDrawingDocument`
 * Zod re-parse (which fabricates fresh references for every entity)
 * are not misclassified as `updated`. Only true shape changes count.
 *
 * This is the same kind of cheap deep-equal used elsewhere in the
 * codebase for property snapshots — sufficient for plain-JSON entity
 * payloads (no Date / Map / RegExp / Symbol).
 */
function entitiesShapeEqual(a: DrawingEntity, b: DrawingEntity): boolean {
  if (a === b) return true;
  // Fast path: id + type mismatch ⇒ different shapes by definition.
  if (a.id !== b.id || a.type !== b.type) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Diff two entity arrays from the same space. Structural-aware (NOT
 * reference-aware) so survivors of a Zod re-parse don't surface as
 * spurious `updated` records. An entity only in `prev` is `removed`;
 * an entity only in `next` is `added`; an entity present in both with
 * a structural shape change is `updated`.
 */
function diffSpaceEntities(
  spaceId: string,
  prev: ReadonlyArray<DrawingEntity>,
  next: ReadonlyArray<DrawingEntity>,
  out: MutableMutation,
): void {
  const prevById = new Map<string, DrawingEntity>();
  for (const ent of prev) prevById.set(ent.id, ent);
  const nextById = new Map<string, DrawingEntity>();
  for (const ent of next) nextById.set(ent.id, ent);

  for (const ent of prev) {
    const after = nextById.get(ent.id);
    if (!after) {
      out.removed.push({ spaceId, entityId: ent.id });
    } else if (!entitiesShapeEqual(ent, after)) {
      out.updated.push({ spaceId, prev: ent, next: after });
    }
  }
  for (const ent of next) {
    if (!prevById.has(ent.id)) {
      out.added.push({ spaceId, entity: ent });
    }
  }
}

/**
 * Diff two whole DrawingDocuments space-by-space. Used for cases that
 * delegate to a helper which rebuilds significant portions of the
 * document and there's no cheap closed-form predicate for what changed.
 *
 * `styleTablesTouched` is set automatically when the helper detects
 * blockRecord / dimensionStyle / etc. table changes between pre/post
 * — passed through to the returned `EntityMutation`.
 *
 * Cost: O(N) where N = total entity count. Acceptable because the
 * caller already paid O(N) inside `assembleDrawingDocument`'s parse.
 */
export function diffDocumentMutations(
  prev: DrawingDocument,
  next: DrawingDocument,
  styleTablesTouched: ReadonlySet<StyleTableTouchKind> = NO_MUTATIONS.styleTablesTouched,
): EntityMutation {
  if (prev === next) return NO_MUTATIONS;
  const out = emptyMutable();
  const nextSpaceById = new Map<string, (typeof next.spaces)[number]>();
  for (const sp of next.spaces) nextSpaceById.set(sp.id, sp);

  for (const prevSpace of prev.spaces) {
    const nextSpace = nextSpaceById.get(prevSpace.id);
    if (!nextSpace) {
      // Whole space disappeared — every entity is `removed`.
      for (const ent of prevSpace.entities) {
        out.removed.push({ spaceId: prevSpace.id, entityId: ent.id });
      }
      continue;
    }
    diffSpaceEntities(prevSpace.id, prevSpace.entities, nextSpace.entities, out);
  }

  const prevSpaceIds = new Set(prev.spaces.map((s) => s.id));
  for (const sp of next.spaces) {
    if (prevSpaceIds.has(sp.id)) continue;
    for (const ent of sp.entities) {
      out.added.push({ spaceId: sp.id, entity: ent });
    }
  }

  return freezeMutation(out, styleTablesTouched);
}

/**
 * PR #362 round-5 (F5) — the SYNTHETIC, TRANSIENT edit-space kinds. A BEDIT /
 * TESTBLOCK / REFEDIT session adds an in-memory space populated with COPIES of
 * the edited block's entities; none of them is persisted to DXF (the writer only
 * emits `model` / `layout`). They are NOT user geometry, so they are excluded
 * from the geometry-class diff (mirroring `documentTableFingerprint`, which
 * excludes them from the TABLE fingerprint).
 */
export const SYNTHETIC_EDIT_SPACE_KINDS: ReadonlySet<string> = new Set([
  "block-edit",
  "block-test",
  "refedit",
]);

/**
 * PR #362 round-5 (F5) — return a view of `document` with the synthetic edit
 * spaces removed, for the geometry-class diff of a `space/*` lifecycle command
 * (forward path) AND the undo/redo/abort whole-document diff (round-6 F5).
 * The non-space fields are shared by reference (immutable); only the `spaces`
 * array is filtered. A persistent `model` / `layout` space is always kept.
 *
 * SINGLE source of truth for the synthetic-space exclusion: both
 * `geometryReducer.ts` (forward `space/*` diff) and `editorStore.ts`
 * (`computeRevisionStateDiff` → undo / redo / abort net-diff) call this, so a
 * synthetic edit-space's copied entities never count as a geometry change on ANY
 * path — consistent with `documentTableFingerprint`'s TABLE-side exclusion.
 */
export function documentWithoutSyntheticSpaces(
  document: DrawingDocument,
): DrawingDocument {
  if (!document.spaces.some((s) => SYNTHETIC_EDIT_SPACE_KINDS.has(s.kind))) {
    return document;
  }
  return {
    ...document,
    spaces: document.spaces.filter(
      (s) => !SYNTHETIC_EDIT_SPACE_KINDS.has(s.kind),
    ),
  };
}

/**
 * Construct a single-entity `added` mutation by reading the appended
 * entity back out of the post-assemble document so the payload
 * reference matches what `commitMutation` will install in the cache
 * (Zod parse breaks reference identity).
 *
 * Used by the ~20 inline create-* cases in `geometryReducer.ts`. The
 * caller knows the new entity's id and spaceId by construction; the
 * helper resolves the canonical reference via id lookup.
 *
 * Returns `NO_MUTATIONS` when the entity cannot be located (defensive —
 * this should not happen in production but a single bad payload should
 * not break the dispatch chain).
 */
export function singleAddedMutation(
  nextDoc: DrawingDocument,
  spaceId: string,
  entityId: string,
): EntityMutation {
  for (const space of nextDoc.spaces) {
    if (space.id !== spaceId) continue;
    const ent = space.entities.find((e) => e.id === entityId);
    if (!ent) return NO_MUTATIONS;
    return {
      added: [{ spaceId, entity: ent }],
      removed: [],
      updated: [],
      styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
    };
  }
  return NO_MUTATIONS;
}

/** One pre-mutation entity snapshot plus the space it must land back in. */
export interface EntitySnapshotReplacement {
  spaceId: string;
  snapshot: DrawingEntityInput;
}

/**
 * The ONE truth behind every snapshot-restore inverse: replace the entity
 * matched by `snapshot.id` in `spaceId` with that parsed snapshot, in place,
 * and report each replacement as an `updated` diff.
 *
 * Two reducer arms need exactly this and differ only in incidentals — the
 * command literal, the entry field name, and (for
 * `geometry/restore-viewport-clip`) an additional document-scope
 * `rawObjectGroups` re-attach the caller performs around this call:
 *
 *   - `geometry/restore-viewport-clip` — the delete inverse for a DANGLING
 *     viewport clip (PR #324 / #318 R2).
 *   - `geometry/restore-entity-snapshot` — the `properties/apply` PRESENCE-BIT
 *     restore (@codex PR #785 round 2, F2).
 *
 * Three invariants live here rather than in either caller:
 *
 *   1. REPLACE, never append. `updateEntityById` locates by id, swaps the row
 *      and preserves id + spaceId (it throws if either changes); appending
 *      would inject a duplicate id.
 *   2. Parse through `drawingEntitySchema`, so the snapshot lands as a
 *      canonical `DrawingEntity` with defaults filled — the same normalization
 *      `appendGeometryEntity` performs.
 *   3. Emit an `updated` diff per replacement, so `commitMutation`'s
 *      compact-cache / index sync RE-LEARNS the restored state. Without it the
 *      returned document is restored while the store stays stale — the same
 *      reason `clearDanglingViewportClips` threads `updated`.
 *
 * A missing target is skipped defensively (`updateEntityById` returns the SAME
 * document reference when no entity carries that id), so a restore aimed at an
 * entity that no longer exists never materialises a phantom.
 */
export function replaceEntitySnapshots(
  document: DrawingDocument,
  entries: readonly EntitySnapshotReplacement[],
): {
  document: DrawingDocument;
  updated: Array<{ spaceId: string; prev: DrawingEntity; next: DrawingEntity }>;
} {
  const updated: Array<{
    spaceId: string;
    prev: DrawingEntity;
    next: DrawingEntity;
  }> = [];
  let nextDoc = document;
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    const snapshot = entry.snapshot;
    const targetId = (snapshot as { id?: string } | null | undefined)?.id;
    const targetSpaceId = entry.spaceId;
    if (!targetId || !targetSpaceId) continue;
    const restored = drawingEntitySchema.parse(snapshot);
    let prevEntity: DrawingEntity | undefined;
    const replacedDoc = updateEntityById(nextDoc, targetId, (existing) => {
      prevEntity = existing;
      return restored;
    });
    if (replacedDoc === nextDoc || !prevEntity) continue;
    nextDoc = replacedDoc;
    updated.push({ spaceId: targetSpaceId, prev: prevEntity, next: restored });
  }
  return { document: nextDoc, updated };
}
